import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/cartSlice';
import { translations } from '../utils/translations';
import AboutHero from './AboutHero';
import './AboutUs.css';

const AboutUs = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? -1 : index);
  };

  const faqData = [
    {
      question: t.faqQ1 || "What services does Smart Telecom Store offer?",
      answer: t.faqA1 || "Smart Telecom Store offers a wide range of premium services, including smartphone and tablet sales (brand-new and certified pre-owned), high-quality repair services (screen replacement, battery diagnostics, motherboard soldering), and a curated collection of tech accessories like cases, cables, chargers, and audio gear."
    },
    {
      question: t.faqQ2 || "How long does a typical mobile repair take?",
      answer: t.faqA2 || "Most standard repairs like screen replacements, battery changes, and charging port fixes are completed within 30 to 60 minutes. You can walk in or book an appointment online. Advanced diagnostics or motherboard repairs may take 1 to 2 business days."
    },
    {
      question: t.faqQ3 || "Do you offer a warranty on repairs and purchases?",
      answer: t.faqA3 || "Absolutely! We offer a 6-month worry-free warranty on all repair services and replacement parts. Brand-new retail products carry their official manufacturer warranties."
    },
    {
      question: t.faqQ4 || "Can I trade in my old device at Smart Telecom Store?",
      answer: t.faqA4 || "Yes, you can! Bring your old phone or tablet to any of our stores. We will run a quick evaluation and offer you competitive store credit or cash that you can apply immediately to upgrade your device."
    }
  ];

  useEffect(() => {
    // 1. Update page title
    document.title = `${t.aboutUs} | Smart Telecom Store`;

    // 2. Manage meta description tag
    let metaDescription = document.querySelector('meta[name="description"]');
    const descriptionText = t.aboutDesc || 'Learn more about Smart Telecom Store, your destination for premium mobile phones, accessories, repair services, and tech accessories. Discover our story, vision, and core values.';

    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }

    const originalContent = metaDescription.content;
    metaDescription.content = descriptionText;

    // Restore original meta on unmount
    return () => {
      document.title = 'Smart Telecom Store | Premium Electronics Store';
      if (metaDescription) {
        metaDescription.content = originalContent || '';
      }
    };
  }, [t]);

  return (
    <div className="about-us-page">
      {/* 1. Rotated Grid Hero Section */}
      <AboutHero />

      {/* 2. Premium Content Sections */}
      <section className="about-content-section">
        <div className="about-content-inner">

          {/* Mission & Vision cards */}
          <div className="about-cards-grid">
            <div className="about-card">
              <div className="about-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
              </div>
              <h3 className="about-card-title">{t.aboutMission}</h3>
              <p className="about-card-text">
                {t.aboutMissionDesc}
              </p>
            </div>

            <div className="about-card">
              <div className="about-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <h3 className="about-card-title">{t.aboutVision}</h3>
              <p className="about-card-text">
                {t.aboutVisionDesc}
              </p>
            </div>

            <div className="about-card">
              <div className="about-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3 className="about-card-title">{t.aboutGuarantee}</h3>
              <p className="about-card-text">
                {t.aboutGuaranteeDesc}
              </p>
            </div>
          </div>

          {/* Detailed Story Section */}
          <div className="about-story-row">
            <div className="about-story-col text-col">
              <span className="story-badge">{t.aboutWhoWeAre}</span>
              <h2 className="about-story-title">{t.aboutStoryTitle}</h2>
              <p className="story-paragraph">
                {t.aboutStoryDesc1}
              </p>
              <p className="story-paragraph">
                {t.aboutStoryDesc2}
              </p>

              <div className="about-stats-row">
                <div className="stat-item">
                  <span className="stat-number">15K+</span>
                  <span className="stat-label">{t.aboutStatsHappy}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">99.8%</span>
                  <span className="stat-label">{t.aboutStatsSuccess}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">{t.aboutStatsSupport}</span>
                </div>
              </div>
            </div>

            <div className="about-story-col visual-col">
              <div className="about-story-image-wrapper">
                <img
                  src="/images/about-moc.jpg"
                  alt="Smart Telecom Store Premium Electronics Shop"
                  className="about-story-img"
                />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 3. Frequently Asked Questions Section */}
      <section className="faq-section">
        <div className="faq-inner">
          {/* Left Column */}
          <div className="faq-left-col">
            <div className="faq-pill-badge">
              <svg className="faq-pill-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span>{t.faqPillBadge}</span>
            </div>
            <h2 className="faq-title">
              {t.faqTitleFirst} <br />
              <span className="faq-title-highlight">{t.faqTitleSecond}</span>
            </h2>
            <p className="faq-description">
              {t.faqSectionDesc}
            </p>
          </div>

          {/* Right Column: Accordion */}
          <div className="faq-right-col">
            <div className="faq-accordion-list">
              {faqData.map((item, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div
                    key={index}
                    className={`faq-accordion-item ${isOpen ? 'active' : ''}`}
                  >
                    <button
                      className="faq-question-btn"
                      onClick={() => toggleFaq(index)}
                      aria-expanded={isOpen}
                    >
                      <span className="faq-question-text">{item.question}</span>
                      <div className="faq-arrow-icon-wrapper">
                        <svg
                          className={`faq-arrow-svg ${isOpen ? 'rotated' : ''}`}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </div>
                    </button>
                    <div className={`faq-answer-collapse ${isOpen ? 'expanded' : ''}`}>
                      <div className="faq-answer-content">
                        <p>{item.answer}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
